import React, { ButtonHTMLAttributes, ReactNode } from 'react';
import styled from 'astroturf/react';

export const Container = styled.button<{
  size: ButtonProps['size'];
  variant: ButtonProps['variant'];
  weight: ButtonProps['weight'];
  icononlybreakpoint: ButtonProps['iconOnlyBreakpoint'];
}>`
  @import 'stylesheets/design_system/font';
  @import 'stylesheets/design_system/_breakpoint.scss';
  @import 'stylesheets/design_system/_color.scss';

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: transparent;
  border: none;
  border-radius: 4px;
  text-wrap: nowrap;
  cursor: pointer;

  // Don't let the icon shrink
  & > :first-child:not(:only-child) {
    flex-shrink: 0;
  }

  &.size-lg {
    height: 54px;
    padding: 8px 20px;
    @include body__medium-medium;

    &.variant-text,
    &.variant-text-gray {
      height: 40px;
      padding: 8px 12px;
      @include sans-serif;
      font-size: 18px;
      line-height: 22px;
      font-weight: 500;

      &.weight-regular {
        font-weight: 400;
      }
    }
  }

  &.size-sm {
    height: 36px;
    padding: 8px 12px;
    @include sans-serif;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;

    &.variant-text,
    &.variant-text-gray {
      height: 40px;
      padding: 8px 12px;
      @include body__medium-medium;

      &.weight-regular {
        font-weight: 400;
      }
    }
  }

  // non-text buttons don't have xs size in design
  &.size-xs {
    height: 36px;
    @include sans-serif;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
  }

  &.variant-primary:not(:disabled) {
    background: $ds-primary-blue;
    color: $ds-white;

    &:hover {
      background: $ds-primary-blue-active;
    }

    &:focus-visible {
      background: $ds-primary-blue-active;
      outline: 2px solid #9bb5e0;
    }

    &:active {
      background: #082f75;
    }
  }

  &.variant-primary-dark:not(:disabled) {
    background: $ds-primary-blue-active;
    color: $ds-white;

    &:hover {
      background: #082f75;
    }

    &:focus-visible {
      background: #082f75;
      outline: 2px solid #9bb5e0;
    }

    &:active {
      background: #043059;
    }
  }

  &.variant-secondary:not(:disabled) {
    background: #eef2f9;
    color: $ds-primary-blue-active;
    border: 1px solid #cddaef;

    &:hover {
      background: #e6ecf7;
    }

    &:focus-visible {
      background: #e6ecf7;
      border: 1px solid #e6ecf7;
      outline: 2px solid #9bb5e0;
    }

    &:active {
      background: #cddaef;
      border: 1px solid #cddaef;
    }
  }

  &.variant-inverse:not(:disabled) {
    background: $ds-white;
    color: $ds-primary-blue-active;
    border: 1px solid #9bb5e0;

    &:hover {
      background: #eef2f9;
    }

    &:focus-visible {
      background: #eef2f9;
      border: 1px solid #eef2f9;
      outline: 2px solid #9bb5e0;
    }

    &:active {
      background: #e6ecf7;
      border: 1px solid #e6ecf7;
    }
  }

  &.variant-inverse-gray:not(:disabled) {
    background: $ds-white;
    color: $ds-gray-dark;
    border: 1px solid #dddde2;

    &:hover {
      background: #eef2f9;
    }

    &:focus-visible {
      background: #eef2f9;
      border: 1px solid #eef2f9;
      outline: 2px solid #9bb5e0;
    }

    &:active {
      background: #e6ecf7;
      border: 1px solid #e6ecf7;
    }
  }

  &.variant-text:not(:disabled) {
    color: $ds-primary-blue-active;

    &:hover {
      background: #eef2f9;
    }

    &:focus {
      background: #eef2f9;
      outline: 2px solid #b4c7e8;
    }

    &:active {
      background: #e6ecf7;
    }
  }

  &.variant-text-gray:not(:disabled) {
    color: $ds-gray-dark;

    &:hover {
      background: #f8f8fb;
    }

    &:focus {
      background: #f8f8fb;
      outline: 2px solid #dddde2;
    }

    &:active {
      background: #ebebee;
    }
  }

  &.icononlybreakpoint-sm {
    & > :not(:first-child) {
      display: none;
    }

    @include for-tablet-up {
      & > :not(:first-child) {
        display: block;
      }
    }
  }

  &.icononlybreakpoint-md {
    & > :not(:first-child) {
      display: none;
    }

    @include for-desktop-sm-up {
      & > :not(:first-child) {
        display: block;
      }
    }
  }

  &.icononlybreakpoint-lg {
    & > :not(:first-child) {
      display: none;
    }

    @include for-desktop-md-up {
      & > :not(:first-child) {
        display: block;
      }
    }
  }

  &:disabled {
    color: #b1b1ba;
  }
`;

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  size?: 'lg' | 'sm' | 'xs';
  variant?:
    | 'primary'
    | 'primary-dark'
    | 'secondary'
    | 'inverse'
    | 'inverse-gray'
    | 'text'
    | 'text-gray';
  weight?: 'medium' | 'regular';
  icon?: ReactNode;
  iconOnlyBreakpoint?: 'sm' | 'md' | 'lg';
};

export const Button: React.FC<ButtonProps> = ({
  size = 'sm',
  variant = 'primary',
  weight = 'medium',
  icon,
  iconOnlyBreakpoint,
  children,
  ...props
}) => {
  return (
    <Container
      size={size}
      variant={variant}
      weight={weight}
      icononlybreakpoint={icon ? iconOnlyBreakpoint : undefined}
      {...props}
    >
      {icon}
      {children && <span>{children}</span>}
    </Container>
  );
};
